/**
 * shellLayout — single source of truth for the app-shell scroll model.
 *
 * P0 punch-list fix: several views (task detail, long lists) overflowed
 * vertically and could not scroll to the bottom because the shell relied
 * on document/body scrolling while various parents in the flex chain
 * clipped or height-locked content.
 *
 * The fix is shell-level, not per-view:
 *   - `.app-shell` is a viewport-locked flex COLUMN (height: 100dvh,
 *     min-height: 0) sized under the fixed TopMenu chrome.
 *   - `.app-shell__scroll` is the ONE dedicated scroll container
 *     (flex: 1 1 auto; min-height: 0; overflow-y: auto) that every
 *     route's main region renders inside — so every view scrolls fully
 *     under the fixed top menu with no per-view patches.
 *   - The Projects kanban keeps its height-locked "window" behavior via
 *     `.app-shell--fixed`, which turns the scroll container's own
 *     scrolling OFF (the kanban owns its in-window scroll instead).
 *
 * These constants/helpers are exported so App.tsx, SignView, TopMenu and
 * tests all agree on the class names — no stringly-typed drift.
 */

/** The <main> shell element class. */
export const SHELL_CLASS = 'app-shell';

/** Modifier for height-locked views (Projects kanban window). */
export const SHELL_FIXED_CLASS = 'app-shell--fixed';

/** The dedicated overflow-y:auto scroll container inside the shell. */
export const SHELL_SCROLL_CLASS = 'app-shell__scroll';

/**
 * Compose the <main> className. `fixed` opts into the height-locked
 * kanban-window variant (scrolling handled inside the view).
 */
export function shellClassName(opts?: { fixed?: boolean }): string {
  return opts?.fixed ? `${SHELL_CLASS} ${SHELL_FIXED_CLASS}` : SHELL_CLASS;
}

/**
 * Current scroll offset of the main content region.
 *
 * Views/components that used to read `window.scrollY` (e.g. the TopMenu
 * scroll-shadow) must now read the dedicated container's scrollTop —
 * the document itself no longer scrolls. Falls back to window.scrollY
 * so contexts without the shell (tests, public pages) keep working.
 */
export function getShellScrollTop(doc: Document = document): number {
  const scroller = doc.querySelector(`.${SHELL_SCROLL_CLASS}`);
  if (scroller) return scroller.scrollTop;
  return doc.defaultView?.scrollY ?? 0;
}
